import React, { type ReactNode } from 'react';
import { type MessageDescriptor, type IntlShape } from 'react-intl-next';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Text } from '@atlaskit/primitives/compiled';
// AFP-2532 TODO: Fix automatic suppressions below
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import {
	type BulkInviteSuccessResponse,
	type BulkInviteFailureResponse,
	type Flag,
	type UserRole,
	type InvitedUser,
	isUserWithEmail,
} from '../../../types';
import {
	CLOUD_ADMIN_URL,
	getProductTitle,
	getBillingUrl,
	adminHubManageUsersHref,
	adminHubManageUserHref,
} from '../../../utils';
import { messages } from '../../i18n';
import { fg } from '@atlaskit/platform-feature-flags';

export const getInvitedUserEmailOrName = (
	responseArray: InvitedUser[],
	getName: (id: string) => string,
): string => {
	if (!responseArray.length) {
		return '';
	}

	return isUserWithEmail(responseArray[0]) ? responseArray[0].email : getName(responseArray[0].id);
};

const getManageUserAccessLink = (
	cloudId: string,
	invited: BulkInviteSuccessResponse['invited'],
) => {
	if (invited.length === 0) {
		return undefined;
	}
	const [user] = invited;
	if (invited.length === 1 && user.id) {
		return adminHubManageUserHref(cloudId, user.id);
	}

	return adminHubManageUsersHref(cloudId);
};

export const getSuccessFlag = (
	result: BulkInviteSuccessResponse,
	cloudId: string,
	userRole: string,
	products: string[],
	getName: (id: string) => string,
	intl: IntlShape,
	onReopenHandler?: () => void,
): Flag | null => {
	const isAdmin = userRole === 'admin';
	const { invited = [], accessRequested = [] } = result;
	let successTitle: MessageDescriptor = messages.genericSuccessFlagTitle;

	if (invited.length || accessRequested.length) {
		let descriptionMessage: MessageDescriptor = messages.hybridSuccessFlagDescription;

		const isInviteOnly = invited.length && !accessRequested.length;
		const isAccessRequestOnly = !invited.length && accessRequested.length;

		if (isInviteOnly) {
			if (products.length > 1 && fg('puma-874-success-flag-fix-for-multiple-products')) {
				descriptionMessage = messages.inviteSuccessFlagDescriptionMultipleProductsEmail;
			} else {
				descriptionMessage = messages.inviteSuccessFlagDescriptionSingleProductEmail;
			}
		} else if (isAccessRequestOnly) {
			descriptionMessage = messages.requestAccessSuccessFlagDescription;
			successTitle = messages.requestAccessSuccessFlagTitle;
		} else if (invited.length === 1 && accessRequested.length === 1) {
			descriptionMessage = messages.hybridSuccessFlagDescriptionOneEach;
			successTitle = messages.hybridSuccessFlagTitle;
		}
		const formattedMessageValues = {
			inviteCount: invited.length,
			requestAccessCount: accessRequested.length,
			emailInvited: getInvitedUserEmailOrName(invited, getName),
			emailRequestedAccess: getInvitedUserEmailOrName(accessRequested, getName),
			product: products.length > 1 ? null : getProductTitle(products[0]),
			productCount: products.length,
			b: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
			p: (chunks: ReactNode) => <Text as="p">{chunks}</Text>,
		};

		let userAccessLink = isAdmin
			? getManageUserAccessLink(cloudId, invited)
			: undefined;
		const actions = [];
		if (userAccessLink) {
			actions.push({
				content: intl.formatMessage(
					invited.length > 1
						? messages.manageUsersAccessActionLabel
						: messages.manageUserAccessActionLabel,
				),
				href: userAccessLink,
				target: '_blank',
			});
		}
		if (onReopenHandler) {
			actions.push({
				content: intl.formatMessage(messages.addMorePeopleActionLabel),
				onClick: onReopenHandler,
			});
		}

		return {
			appearance: 'success',
			icon: (
				<SuccessIcon
					label="success"
					spacing="spacious"
					color={token('color.icon.success', colors.G400)}
				/>
			),
			title: intl.formatMessage(successTitle),
			description: intl.formatMessage(descriptionMessage, {
				...formattedMessageValues,
				invitedHybridPluralCoreInvitesOptimisation: intl.formatMessage(
					messages.invitedHybridPlural,
					formattedMessageValues,
				),
				requestAccessHybridPluralCoreInvitesOptimisation: intl.formatMessage(
					messages.requestAccessHybridPlural,
					formattedMessageValues,
				),
			}),
			actions,
		};
	}

	return null;
};

export const getErrorFlag = (
	result: BulkInviteSuccessResponse,
	userRole: UserRole,
	getName: (id: string) => string,
	intl: IntlShape,
): Flag | null => {
	const { error } = result;
	const isAdmin = userRole === 'admin';

	if (error && error.length) {
		return {
			appearance: 'error',
			icon: (
				<ErrorIcon
					label="error"
					spacing="spacious"
					color={token('color.icon.danger', colors.R400)}
				/>
			),
			title: intl.formatMessage(messages.inviteFailedFlagTitle, {
				isAdmin,
				failedInviteCount: error.length,
			}),
			description: intl.formatMessage(messages.inviteFailedFlagDescription, {
				isAdmin,
				emailFailedToInvite: getInvitedUserEmailOrName(error, getName),
				failedInviteCount: error.length,
				b: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
			}),
			actions: isAdmin
				? [
						{
							content: intl.formatMessage(messages.contactSupportActionLabel),
							href: 'https://support.atlassian.com/contact/',
							target: '_blank',
						},
					]
				: [],
		};
	}

	return null;
};

export const getGenericErrorFlag = (userRole: UserRole, intl: IntlShape): Flag => {
	const isAdmin = userRole === 'admin';

	return {
		appearance: 'error',
		icon: (
			<ErrorIcon label="error" spacing="spacious" color={token('color.icon.danger', colors.R400)} />
		),
		title: intl.formatMessage(messages.genericFailedFlagTitle),
		description: intl.formatMessage(messages.genericFailedFlagDescription, {
			isAdmin,
		}),
		actions: isAdmin
			? [
					{
						content: intl.formatMessage(messages.contactSupportActionLabel),
						href: 'https://support.atlassian.com/contact/',
						target: '_blank',
					},
				]
			: [],
	};
};

export const getFailureFlag = (
	result: BulkInviteFailureResponse,
	cloudId: string,
	userRole: UserRole,
	failedUsers: InvitedUser[],
	products: string[],
	intl: IntlShape,
): Flag | null => {
	const { failure } = result;
	if (!failure) {
		return null;
	}
	if (failure.code !== 'licence-exceeded') {
		return getGenericErrorFlag(userRole, intl);
	}
	const billingUrl = getBillingUrl(products, cloudId);
	return {
		appearance: 'warning',
		icon: <PeopleGroupIcon label={failure.code} color={token('color.icon.warning', colors.Y300)} />,
		title: intl.formatMessage(messages.licenceExceededFlagTitle, {
			failedInviteCount: failedUsers.length,
		}),
		description: intl.formatMessage(messages.licenceExceededFlagDescription, {
			failedInviteCount: failedUsers.length,
			userRole,
		}),
		actions:
			userRole === 'admin'
				? [
						{
							content: intl.formatMessage(messages.upgradePlanActionLabel),
							href: billingUrl,
							target: '_blank',
						},
						{
							content: intl.formatMessage(messages.viewUsersActionLabel),
							href: `${CLOUD_ADMIN_URL}/s/${cloudId}/users`,
							target: '_blank',
						},
					]
				: [],
	};
};
